import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { 
  AlertTriangle, CheckCircle, ExternalLink, Filter, 
  AlertOctagon, AlertCircle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getViolations, resolveViolation } from '../services/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const ViolationsPage = () => {
  const { refreshStats } = useOutletContext();
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    fetchViolations();
  }, []);

  const fetchViolations = async () => {
    try {
      const response = await getViolations();
      setViolations(response.data);
    } catch (error) {
      console.error('Failed to fetch violations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (violationId) => {
    try {
      await resolveViolation(violationId);
      toast.success('Violation marked as resolved');
      fetchViolations();
      refreshStats();
    } catch (error) {
      toast.error('Failed to resolve violation');
    }
  };

  const filteredViolations = violations.filter((v) => {
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
    const matchesSeverity = severityFilter === 'all' || v.severity === severityFilter;
    return matchesStatus && matchesSeverity;
  });

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertOctagon className="w-5 h-5 text-[#FF3B30]" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-[#FF6961]" />;
      case 'medium': return <AlertCircle className="w-5 h-5 text-[#FFCC00]" />;
      default: return <Info className="w-5 h-5 text-[#0066FF]" />;
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'risk-critical';
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      default: return 'risk-low';
    }
  };

  const getViolationTypeLabel = (type) => {
    const labels = {
      'unapproved_tool': 'Unapproved Tool Usage',
      'excessive_privilege': 'Excessive Privileges',
      'missing_owner': 'Missing Owner',
      'prod_write_from_dev': 'Production Write from Dev',
      'sensitive_data_access': 'Sensitive Data Access',
      'orphaned_identity': 'Orphaned Identity',
      'shared_credential': 'Shared Credential',
    };
    return labels[type] || type;
  };

  const stats = {
    total: violations.length,
    open: violations.filter(v => v.status === 'open').length,
    critical: violations.filter(v => v.severity === 'critical' && v.status === 'open').length,
    high: violations.filter(v => v.severity === 'high' && v.status === 'open').length,
  };

  return (
    <div data-testid="violations-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Policy Violations
        </h1>
        <p className="text-[#9CA3AF]">
          Monitor and remediate policy violations across your AI agent ecosystem.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="enterprise-card p-6">
          <div className="stat-value text-[#F9FAFB]">{stats.total}</div>
          <div className="stat-label">Total Violations</div>
        </div>
        <div className="enterprise-card p-6">
          <div className="stat-value text-[#FFCC00]">{stats.open}</div>
          <div className="stat-label">Open</div>
        </div>
        <div className="enterprise-card p-6">
          <div className="stat-value text-[#FF3B30]">{stats.critical}</div>
          <div className="stat-label">Critical</div>
        </div>
        <div className="enterprise-card p-6">
          <div className="stat-value text-[#FF6961]">{stats.high}</div>
          <div className="stat-label">High</div>
        </div>
      </div>

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-[#6B7280]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="severity-filter">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Violations List */}
      <div className="space-y-4">
        {loading ? (
          <div className="enterprise-card p-8 text-center text-[#9CA3AF]">Loading violations...</div>
        ) : filteredViolations.length === 0 ? (
          <div className="enterprise-card p-8 text-center">
            <CheckCircle className="w-12 h-12 text-[#34C759] mx-auto mb-4" />
            <p className="text-[#9CA3AF]">No violations found matching your criteria.</p>
          </div>
        ) : (
          filteredViolations.map((violation) => (
            <div 
              key={violation.id} 
              className={`enterprise-card p-6 border-l-4 ${
                violation.severity === 'critical' ? 'border-[#FF3B30]' :
                violation.severity === 'high' ? 'border-[#FF6961]' :
                violation.severity === 'medium' ? 'border-[#FFCC00]' :
                'border-[#0066FF]'
              }`}
              data-testid={`violation-${violation.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {getSeverityIcon(violation.severity)}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={getSeverityClass(violation.severity)}>
                        {violation.severity}
                      </span>
                      <span className="text-[#6B7280] text-sm">
                        {getViolationTypeLabel(violation.violation_type)}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-mono ${
                        violation.status === 'open' 
                          ? 'text-[#FFCC00] bg-[#FFCC00]/10' 
                          : 'text-[#34C759] bg-[#34C759]/10'
                      }`}>
                        {violation.status}
                      </span>
                    </div>
                    <p className="text-[#F9FAFB] mb-2">{violation.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <Link 
                        to={`/dashboard/agents/${violation.agent_id}`}
                        className="text-[#0066FF] hover:text-[#3385FF] flex items-center gap-1"
                      >
                        View Agent <ExternalLink className="w-3 h-3" />
                      </Link>
                      <span className="text-[#6B7280]">
                        Detected: {new Date(violation.detected_at).toLocaleString()}
                      </span>
                    </div>
                    {violation.evidence && (
                      <div className="mt-3 p-3 bg-[#1A1A1A] border border-[#27272A]/40">
                        <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Evidence</div>
                        <pre className="text-xs font-mono text-[#9CA3AF] overflow-x-auto">
                          {JSON.stringify(violation.evidence, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
                {violation.status === 'open' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="secondary-btn"
                        data-testid={`resolve-violation-${violation.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#F9FAFB]">Resolve Violation?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#9CA3AF]">
                          Mark this violation as resolved. This action will be logged in the audit trail.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="primary-btn"
                          onClick={() => handleResolve(violation.id)}
                        >
                          Confirm Resolve
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ViolationsPage;
